import React from 'react';
import { useIntl } from 'react-intl';
import { youtubeWatchUrl } from 'fym-shared/src/music24/addedAssets';
import { TypedMemo } from '../../../../../common/types';
import type { CatalogItem } from '../../../../../../redux/api/fymAPI.types';
import { SMLButton } from '../../../../../music24/components/primitives/SMLButton';
import { isSoundMismatch } from '../../../../../music24/promote/addedAssets/addedAssetFacts';
import { MusicVideoBox } from './MusicVideoBox';
import { musicVideoFieldMessages as messages } from './musicVideoFieldMessages';
import type { MusicVideoFieldState } from './musicVideoFieldState';
import { useSongMusicVideo } from './useSongMusicVideo';
import { DirectedVideoEntry } from '../../../../../directedVideo/entry/DirectedVideoEntry';
import { useDirectedVideoEntry } from '../../../../../directedVideo/entry/useDirectedVideoEntry';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the song page; each part uses its own slice of it */
import pageStyles from '../SongPage.module.scss';
/* eslint-enable css-modules/no-unused-class */
import styles from './SongMusicVideoField.module.scss';

interface SongMusicVideoFieldProps {
  accountId: number;
  item: CatalogItem;
  /** The demo artist: the buttons are off (design g2 note). */
  readOnly: boolean;
}

const LABELS: Readonly<Record<MusicVideoFieldState, keyof typeof messages>> = {
  none: 'label',
  youtube: 'labelYouTube',
  file: 'labelFile',
  made: 'labelMade',
  making: 'labelMade',
  failed: 'label',
};

/**
 * The music video of a song, next to the cover art (design music-video g1, g2; spec 2026-10-09 D1):
 * one video per song. Empty: "No music video yet", "Add your video" (the dialog of frame a2) and
 * "Make one" (the editor). Filled: the 16:9 box with play, and the actions of its source. The
 * native app's web view does not show it (the apps show their own row, §4.7).
 */
const SongMusicVideoFieldComponent: React.FC<SongMusicVideoFieldProps> = ({ accountId, item, readOnly }) => {
  const intl = useIntl();
  const { summary, state, badge, downloadUrl, openAdd, openEditor } = useSongMusicVideo({ accountId, item });
  const youtube = summary?.youtube ?? null;
  // The directed video beta (design directed-video a1, a2): only with `canRenderFullSongVideos`.
  const directed = useDirectedVideoEntry({
    accountId,
    catalogItemUuid: item.uuid,
    song: {
      hasMaster: item.song.hasMaster,
      masterReady: item.song.masterStatus === 'ready',
      hasLyrics: item.song.hasLyrics,
    },
  });
  const youtubeUrl = youtube === null ? undefined : youtubeWatchUrl(youtube.videoId);

  const twoButtons = (first: React.ReactNode, second: React.ReactNode) => (
    <div className={styles.row}>
      {first}
      {second}
    </div>
  );

  let actions: React.ReactNode;
  let hint: React.ReactNode = null;
  if (state === 'none' || state === 'failed') {
    actions = twoButtons(
      <SMLButton onClick={() => openAdd()} disabled={readOnly}>
        {intl.formatMessage(messages.addYourVideo)}
      </SMLButton>,
      <SMLButton variant="primary" onClick={openEditor} disabled={readOnly}>
        {intl.formatMessage(messages.makeOne)}
      </SMLButton>
    );
    // With the directed video entry, its hint names both ways (design a1).
    if (!(directed.visible && state === 'none')) {
      hint = intl.formatMessage(state === 'none' ? messages.hintNone : messages.failed);
    }
  } else if (state === 'youtube') {
    actions = twoButtons(
      <SMLButton href={youtubeUrl}>{intl.formatMessage(messages.openOnYouTube)}</SMLButton>,
      <SMLButton onClick={() => openAdd('youtube')} disabled={readOnly}>
        {intl.formatMessage(messages.change)}
      </SMLButton>
    );
    hint = readOnly ? (
      intl.formatMessage(messages.hintYouTube)
    ) : (
      <button type="button" className={pageStyles.textButton} onClick={() => openAdd('upload')}>
        {intl.formatMessage(messages.hintYouTube)}
      </button>
    );
  } else if (state === 'file') {
    actions = twoButtons(
      <SMLButton onClick={() => openAdd('upload')} disabled={readOnly}>
        {intl.formatMessage(messages.replace)}
      </SMLButton>,
      downloadUrl === null ? null : <SMLButton href={downloadUrl}>{intl.formatMessage(messages.download)}</SMLButton>
    );
    hint = intl.formatMessage(messages.hintFile);
  } else {
    actions = twoButtons(
      <SMLButton variant="primary" onClick={openEditor} disabled={readOnly}>
        {intl.formatMessage(messages.editVideo)}
      </SMLButton>,
      <SMLButton onClick={() => openAdd('upload')} disabled={readOnly}>
        {intl.formatMessage(messages.replaceWithFile)}
      </SMLButton>
    );
  }

  return (
    <div className={styles.field} data-testid="song-music-video-field" data-state={state}>
      <span className={pageStyles.label}>{intl.formatMessage(messages[LABELS[state]])}</span>
      {summary === null || state === 'failed' ? (
        <div className={`${styles.box} ${styles.empty}`}>{intl.formatMessage(messages.empty)}</div>
      ) : (
        <MusicVideoBox accountId={accountId} catalogItemUuid={item.uuid} summary={summary} badge={badge} />
      )}
      {state === 'made' && <span className={styles.made}>{intl.formatMessage(messages.madeLine)}</span>}
      {state === 'making' && <span className={pageStyles.help}>{intl.formatMessage(messages.making)}</span>}
      {state === 'file' && isSoundMismatch(summary?.soundCheck) && (
        <span className={styles.warn}>{intl.formatMessage(messages.soundMismatch, { title: item.title })}</span>
      )}
      {actions}
      {directed.visible && (
        <DirectedVideoEntry
          open={directed.open}
          readOnly={readOnly}
          onStart={directed.start}
          needsPlan={directed.needsPlan}
        />
      )}
      {hint !== null && <span className={pageStyles.help}>{hint}</span>}
    </div>
  );
};

export const SongMusicVideoField = TypedMemo(SongMusicVideoFieldComponent);
